import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import logger from '../utils/logger';

// SendGrid dynamic template IDs — these are template identifiers, not credentials.
// They are safe to keep in source: they require a full SendGrid API key to use.
export const EMAIL_TEMPLATES = {
  PENDING_APPROVAL: 'd-5159567786f64a13807f57d640cef337',
  RELATIONSHIP_CONFIRMATION: 'd-59f79b38fadb4b4d9e9ce8c1816d9124',
  WELCOME_NEW_MEMBER: 'd-e0820312e22e486c954318aa1f7c3563',
  APPROVAL_GRANTED: 'd-9d5024454368476ba2212687498076e8',
  ACCOUNT_RECOVERY: 'd-4a3157e2d8e449db829df983c6d15e77'
};

export const sendEmailNotification = async (templateId, data) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to send emails');
    }

    const functions = getFunctions();
    const sendEmail = httpsCallable(functions, 'sendEmail');

    const result = await sendEmail({
      templateId,
      to: data.to,
      dynamicData: data
    });

    if (!result.data.success) {
      throw new Error('Failed to send email');
    }

    return true;
  } catch (error) {
    logger.error('Error sending email notification:', error);
    throw error;
  }
};

export const notifyFamilyOwner = async (familyCode, notificationType, data) => {
  try {
    // Get family profile to find owner
    const familyRef = doc(db, 'profiles', familyCode);
    const familyDoc = await getDoc(familyRef);
    
    if (!familyDoc.exists()) {
      throw new Error('Family not found');
    }

    const ownerRef = doc(db, 'users', familyDoc.data().ownerId);
    const ownerDoc = await getDoc(ownerRef);
    
    if (!ownerDoc.exists()) {
      throw new Error('Family owner not found');
    }

    // Send notification to owner
    await sendEmailNotification(notificationType, {
      to: ownerDoc.data().email,
      familyName: familyDoc.data().name,
      ...data
    });

    return true;
  } catch (error) {
    logger.error('Error notifying family owner:', error);
    return false;
  }
};

export const notifyMainContacts = async (familyCode, notificationType, data) => {
  try {
    // Get all main contacts
    const membersRef = doc(db, `profiles/${familyCode}/members`);
    const membersDoc = await getDoc(membersRef);
    
    if (!membersDoc.exists()) {
      throw new Error('No family members found');
    }

    const mainContacts = membersDoc.data().filter(member => member.isMainContact);

    // Send notifications to all main contacts
    await Promise.all(mainContacts.map(contact => 
      sendEmailNotification(notificationType, {
        to: contact.email,
        ...data
      })
    ));

    return true;
  } catch (error) {
    logger.error('Error notifying main contacts:', error);
    return false;
  }
};

export const sendWelcomeEmail = async (userId, familyCode) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const familyRef = doc(db, 'profiles', familyCode);
    const familyDoc = await getDoc(familyRef);
    
    if (!familyDoc.exists()) {
      throw new Error('Family not found');
    }

    await sendEmailNotification(EMAIL_TEMPLATES.WELCOME_NEW_MEMBER, {
      to: userDoc.data().email,
      firstName: userDoc.data().firstName,
      familyName: familyDoc.data().name,
      familyCode: familyCode,
      setupLink: `${window.location.origin}/complete-profile/${userId}`
    });

    return true;
  } catch (error) {
    logger.error('Error sending welcome email:', error);
    return false;
  }
};

export const notifyRelationshipConfirmation = async (userId, relatedUserId, relationship) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    const relatedUserRef = doc(db, 'users', relatedUserId);
    const relatedUserDoc = await getDoc(relatedUserRef);
    
    if (!userDoc.exists() || !relatedUserDoc.exists()) {
      throw new Error('One or both users not found');
    }

    await sendEmailNotification(EMAIL_TEMPLATES.RELATIONSHIP_CONFIRMATION, {
      to: relatedUserDoc.data().email,
      requesterName: `${userDoc.data().firstName} ${userDoc.data().lastName}`,
      relationship: relationship,
      confirmationLink: `${window.location.origin}/confirm-relationship/${userId}`
    });

    return true;
  } catch (error) {
    logger.error('Error sending relationship confirmation:', error);
    return false;
  }
}; 